/**
 * Best-effort Unix mbox splitting, byte-for-byte.
 *
 * Envelope lines start with "From " at the beginning of the file or after a
 * line terminator. Bodies may contain lines that look like envelopes in
 * non-conforming archives; this uses the common `\\n(?=From )` split, widened
 * to CRLF and to the bare CR of a classic-Mac archive so those split the way a
 * Unix one does.
 *
 * NOTHING HERE REWRITES THE MESSAGE. Every string handed back is a slice of the
 * input, and the only bytes dropped are framing: the `From <addr> <date>`
 * envelope line a block was found by, and the blank lines an mbox writer put
 * between blocks. This is the file-ingestion path, so what these functions
 * return is what the app holds, hashes, sizes, reports and exports.
 *
 * These three passes used to run over the input first:
 *
 *     .replace(/\\r\\n/g, "\\n").replace(/\\r/g, "\\n")
 *
 * and they cost more than the octets. A single `.eml` is not an mbox and is
 * still handed through here, so a CRLF file (which is every message written by
 * the RFC 5322 §2.1 line-ending contract) arrived one octet per line shorter
 * than the file the reader opened, and the SHA-256 printed beside the size was
 * over those short bytes: `sha256sum` on the reader's own copy disagreed with
 * the digest this tool showed for it, and no surface said why. The size half of
 * that was measured at 336 octets on disk against 322 held.
 *
 * The parser is the reason this is a loss rather than a wash. `parseEmail`
 * normalises line endings itself, on its own copy, precisely so it can keep the
 * original: `syntax.hasCrlfLineEndings` and the RAW_INVALID_LINE_ENDING defect
 * (bare CR, or LF mixed with CRLF) are read off the UNNORMALISED source. Doing
 * the collapse up here did not save the parser a pass; it fed the parser a
 * source with the evidence already erased, so a message with correct CRLF
 * endings reported `hasCrlfLineEndings: false`, and a legacy or mixed-ending
 * file, the shape that defect exists to surface, could never raise it from a
 * file at all.
 *
 * Disclosure was the other way to close this: keep the rewrite, and say on
 * every surface carrying a size or a digest that both describe a normalised
 * copy. It was rejected because it is a worse deal for the reader in the same
 * breath as being more work: the number still would not reproduce against the
 * file, in two locales, forever.
 *
 * The same claim had a second, quieter counter-example: every block used to
 * come off `.trimEnd()`, which strips ALL trailing whitespace, not just the
 * `\r`/`\n` an mbox writer put there as framing. A body whose last line
 * legitimately ends in a space or a tab lost those octets on the way in, so
 * "the only bytes dropped are framing" was not true of the code, only of the
 * intent. `trimTrailingFraming` strips just the trailing `[\r\n]+` run and
 * leaves SP/HTAB alone, which is what "byte-for-byte" already promised.
 */

/**
 * A line terminator that begins a new mbox block. One source of truth: the
 * eager and streaming splitters must agree on where a message starts, and
 * `iterMbox` builds its global copy from this one.
 *
 * The `\r\n` alternative is deliberately redundant and no test can kill
 * dropping it, which is worth saying rather than leaving for the next person to
 * rediscover. `(?:\r|\n)` would find the same split points: at a CRLF the `\r`
 * branch fails its lookahead and the `\n` branch matches one character later,
 * ending in the same place, and the `\r` it leaves on the tail of the previous
 * block is removed by that block's `trimTrailingFraming` before anyone sees
 * it. It is spelled out anyway because a CRLF is ONE line ending (the same
 * point `email-parser.ts` calls load-bearing where it collapses `\r+\n`), and
 * because the equivalence is an accident of `trimTrailingFraming` rather than
 * a property of the split: an edit that stopped trimming block tails would
 * turn the shorter form into a bug with no failing test to announce it.
 */
const ENVELOPE_BOUNDARY = /(?:\r\n|\r|\n)(?=From )/;

/**
 * Drop only the mbox framing at the end of a block: the run of line
 * terminators an mbox writer left as the blank-line separator before the
 * next envelope, or at the end of the file. `.trimEnd()` strips every
 * trailing whitespace character, space and tab included, and a message
 * whose last body line legitimately ends in one loses payload bytes that
 * were never framing. Only `\r`/`\n` are framing; a trailing SP or HTAB is
 * content and this leaves it alone.
 */
function trimTrailingFraming(s: string): string {
  return s.replace(/[\r\n]+$/, "");
}

export function splitMbox(raw: string): string[] {
  const s = raw.replace(/^\uFEFF/, "");
  const t = trimTrailingFraming(s);
  if (!t.startsWith("From ")) {
    return [s];
  }
  return t.split(ENVELOPE_BOUNDARY).map((p) => trimTrailingFraming(p));
}

/**
 * Catalog H11 — streaming mbox split. Yields one message at a time
 * without materializing the full message array up-front. Used by
 * Phase 6 corpus mode where a single mbox can carry hundreds of
 * megabytes of messages and the consumer can parse + drop each one
 * incrementally.
 *
 * Implementation: finds the offsets where an envelope line begins and slices
 * per yield, so the caller can parse and free each message before the next one
 * is produced. Byte-for-byte with {@link splitMbox}, and pinned to it by test:
 * this is the function the plain-text ingest path actually calls, so a drift
 * between the two would be a drift between what a `.mbox` holds and what a
 * `.eml` inside a `.zip` holds.
 *
 * Compatibility: input is a `string` here for simplicity (matches
 * {@link splitMbox}); a `Uint8Array` overload can ship later when the
 * file-loader stops decoding eagerly.
 */
export function* iterMbox(raw: string): Generator<string, void, void> {
  const s = raw.replace(/^\uFEFF/, "");
  // A file holding nothing but whitespace is coded as no message at all
  // (`loadEmailFile` then reports "empty" rather than filing one made of
  // spaces), which is a different question from what counts as framing on a
  // real message and has to stay a full, generic blank check: `s.trim()`,
  // not `trimTrailingFraming(s)`. Folding the two into one trim is exactly
  // how the SP/HTAB bug this function now avoids got in: a check that
  // decides "is there anything here" was reused to decide "what bytes do we
  // keep", and a body's own trailing SP/HTAB looked like nothing to the
  // first question but was payload to the second.
  if (s.trim().length === 0) return;
  if (!s.startsWith("From ")) {
    yield s;
    return;
  }
  // Find indices where a new envelope starts (the offset just past a line
  // terminator that "From " follows). We don't materialize the full string
  // array — we walk the indices and slice per yield so the caller can free
  // each message before the next one is produced.
  const fromRe = new RegExp(ENVELOPE_BOUNDARY, "g");
  const starts: number[] = [0];
  let m: RegExpExecArray | null;
  while ((m = fromRe.exec(s)) !== null) {
    // Past the WHOLE terminator, not one character past its start: a CRLF
    // archive would otherwise hand every block after the first a leading `\n`.
    starts.push(m.index + m[0].length);
  }
  starts.push(s.length);
  for (let i = 0; i < starts.length - 1; i++) {
    yield trimTrailingFraming(s.slice(starts[i]!, starts[i + 1]!));
  }
}

/**
 * Remove the mbox transport `From <addr> <date>` envelope line if present, so
 * the remainder is RFC 5322 (first line of headers, not the mbox separator).
 *
 * The remainder is returned as a slice. When there is no envelope line the
 * input comes back unchanged, which is what {@link mboxBlockToMessage} reads to
 * tell a framed block from a file that was never framed.
 */
export function stripMboxEnvelope(s: string): string {
  const t = s.replace(/^\uFEFF/, "");
  const term = /\r\n|\r|\n/.exec(t);
  if (!term) {
    return /^From .+/.test(t.trim()) ? "" : t;
  }
  const first = t.slice(0, term.index);
  if (/^From .+/.test(first)) {
    return t.slice(term.index + term[0].length);
  }
  return t;
}

/**
 * Turn one mbox block into the RFC 5322 message inside it.
 *
 * `splitMbox` and `iterMbox` both yield blocks that still carry their
 * `From <addr> <date>` envelope line, because that line is how the block was
 * found and a caller filing raw evidence may want it. Every caller that
 * PARSES a block wants it gone: the envelope is not a header field, so a
 * parser reads `From alice@example Mon Jan  1 …` as a malformed first header
 * and the message's real header block starts one line late.
 *
 * The mboxrd `>From ` quoting is undone at the same time and for the same
 * reason: a writer escaped those body lines precisely so the splitter would
 * not treat them as envelopes, and leaving them escaped means the body this
 * tool shows and hashes is not the body that was sent.
 *
 * It is undone ONLY for a block that carried an envelope line, and that
 * condition is the whole point rather than an optimisation. Both splitters hand
 * a plain `.eml` straight through, so this function is also what a single file
 * passes on its way in. mboxrd quoting exists because of mbox framing; a file
 * with no envelope line was never framed and was never escaped, so a `>From `
 * line in it is the reader's own quoted reply of a line that began "From ", and
 * unquoting it would silently delete a `>` from the body of evidence.
 */
export function mboxBlockToMessage(block: string): string {
  const t = block.replace(/^\uFEFF/, "");
  const stripped = stripMboxEnvelope(t);
  return stripped === t ? t : unescapeMboxrd(stripped);
}

/** Undo mboxrd quoting so message bodies are byte-faithful: ">From " → "From ". */
export function unescapeMboxrd(s: string): string {
  return s.replace(/^>(>*From )/gm, "$1");
}

/**
 * If the buffer looks like mbox, return the first message only and envelope count.
 * Otherwise returns the source unchanged and mboxCount 0.
 */
export function prepareMboxSource(raw: string): {
  text: string;
  mboxCount: number;
} {
  const s = raw.replace(/^\uFEFF/, "");
  if (!s.trimStart().startsWith("From ")) {
    return { text: s, mboxCount: 0 };
  }
  const parts = splitMbox(raw);
  return {
    text: stripMboxEnvelope(parts[0] ?? ""),
    mboxCount: parts.length,
  };
}
